import React from 'react'
import { Check, ExternalLink, StarOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../ui/context-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/tooltip'
import type { SidebarItemProps } from '../SidebarItem.types'

export interface FavoriteTaskItemProps {
  title: string
  completed: boolean
  /** Wo die Aufgabe steht — ihr Projekt oder „Kurz notiert“. Steht im Tooltip. */
  place: string
  /** Wird gerade gezogen — sieht aus wie beim Überfahren. */
  dragging?: boolean
  dragHandle?: SidebarItemProps['dragHandle']
  /** Der Sprung an ihren Platz (Klick; mit ⌘/Strg in neuem Tab). */
  onOpen: (e: React.MouseEvent) => void
  onOpenInNewTab: () => void
  onRemove: () => void
}

/** Das Zeichen vorn: offen ein Kreis, erledigt ein Haken — nur Anzeige, kein Knopf. */
export const FavoriteTaskSign: React.FC<{ completed: boolean }> = ({ completed }) => (
  <span className={`favorite-task-item__sign${completed ? ' favorite-task-item__sign--done' : ''}`} aria-hidden="true">
    {completed && <Check size={10} strokeWidth={3} />}
  </span>
)

/**
 * **Eine Aufgabe in „Favoriten“** (#210, CONTEXT.md → Favorit): ein **Eintrag**, keine Aufgabenzeile.
 * Der Klick springt an ihren Platz, ausgewählt, mit offenen Aufgaben-Details. Vorn zeigt ein Zeichen
 * offen oder erledigt (Variante A im Prototyp) — abhaken lässt sie sich hier nicht; erledigt bleibt
 * sie stehen, durchgestrichen.
 *
 * @example
 * <FavoriteTaskItem title="Kartons besorgen" completed={false} place="Umzug"
 *   onOpen={jump} onOpenInNewTab={jumpInNewTab} onRemove={unstar} />
 */
export const FavoriteTaskItem: React.FC<FavoriteTaskItemProps> = ({
  title, completed, place, dragging = false, dragHandle, onOpen, onOpenInNewTab, onRemove,
}) => {
  const { t } = useTranslation()
  return (
    <ContextMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <ContextMenuTrigger asChild>
            <div
              ref={dragHandle?.ref}
              onPointerDown={dragHandle?.onPointerDown}
              className={`sidebar-item sidebar-item--project-icon favorite-task-item${completed ? ' favorite-task-item--done' : ''}${dragging ? ' sidebar-item--dragging' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={`${title} · ${completed ? t('favorites.statusDone') : t('favorites.statusOpen')}`}
              onClick={onOpen}
              onKeyDown={e => {
                if (e.key === 'Enter') onOpen(e as unknown as React.MouseEvent)
              }}
            >
              <span className="sidebar-item__icon sidebar-item__icon--project">
                <FavoriteTaskSign completed={completed} />
              </span>
              <span className="sidebar-item__label">{title}</span>
            </div>
          </ContextMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{place}</TooltipContent>
      </Tooltip>
      <ContextMenuContent>
        <ContextMenuItem onSelect={onRemove}>
          <StarOff size={14} className="context-menu-content__item-icon" />
          {t('favorites.remove')}
        </ContextMenuItem>
        <ContextMenuItem onSelect={onOpenInNewTab}>
          <ExternalLink size={14} className="context-menu-content__item-icon" />
          {t('favorites.openInNewTab')}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}
